import { apiService } from '@shared/services/apiServices';
import certificateEndpoints from './certificateEndpoints';
import type {
  CertificateListItem,
  CertificatesListResponse,
  ListCertificatesParams,
} from '../types/myCertificateTypes';

const buildEmpty = (page: number, limit: number): CertificatesListResponse => ({
  message: '',
  data: { items: [], total: 0, page, limit },
});

/**
 * Fetches a paginated list of generated certificates for the authenticated
 * caller. TECHNICIAN sees their own; CLIENT sees theirs; ADMIN/SUPER_ADMIN
 * see all.
 *
 * @param params - optional filters: page, limit, facilityIds, startDate, endDate, certificateId
 * @returns Normalised response with items array and pagination total.
 */
export async function listCertificates(
  params: ListCertificatesParams = {}
): Promise<CertificatesListResponse> {
  const qs = new URLSearchParams();
  if (params.page) {
    qs.append('page', String(params.page));
  }
  if (params.limit) {
    qs.append('limit', String(params.limit));
  }
  if (params.facilityIds && params.facilityIds.length > 0) {
    qs.append('facilityIds', params.facilityIds.join(','));
  }
  if (params.equipmentIds && params.equipmentIds.length > 0) {
    qs.append('equipmentIds', params.equipmentIds.join(','));
  }
  if (params.technicianId) {
    qs.append('technicianId', params.technicianId);
  }
  if (params.technicianIds && params.technicianIds.length > 0) {
    qs.append('technicianIds', params.technicianIds.join(','));
  }
  if (params.startDate) {
    qs.append('startDate', params.startDate);
  }
  if (params.endDate) {
    qs.append('endDate', params.endDate);
  }
  if (params.certificateId) {
    qs.append('certificateId', params.certificateId);
  }
  if (params.sortOrder) {
    qs.append('sortOrder', params.sortOrder);
  }

  const fallbackPage = params.page ?? 1;
  const fallbackLimit = params.limit ?? 10;
  const endpoint = `${certificateEndpoints.LIST}${qs.toString() ? `?${qs.toString()}` : ''}`;

  const response = await apiService.getAPI(endpoint, {
    skipGlobalErrorRedirect: true,
  });
  if (response.error) {
    if (response.status === 403) {
      throw Object.assign(new Error('Forbidden'), { status: 403 });
    }
    return buildEmpty(fallbackPage, fallbackLimit);
  }

  const payload = (response.data ?? {}) as Record<string, unknown>;
  const inner = (payload['data'] ?? payload) as Record<string, unknown>;
  const items = Array.isArray(inner['items'])
    ? (inner['items'] as CertificateListItem[])
    : [];

  return {
    message: typeof payload['message'] === 'string' ? payload['message'] : '',
    data: {
      items,
      total: Number(inner['total'] ?? 0),
      page: Number(inner['page'] ?? fallbackPage),
      limit: Number(inner['limit'] ?? fallbackLimit),
    },
  };
}
